import { ActionMeta, GetOptionValue, OnChangeValue, SingleValue } from 'chakra-react-select';

import { InterpolateElement } from '@oaknorth/util-intl';

import { SelectItemSelect } from './SelectItemSelect';
import { SelectItem, SelectItemSelectProps } from './types';

interface OptionsGroup<OptionType> {
  groupLabel: string | InterpolateElement;
  options: OptionType[];
}

/*
 * To be used with SingleItemSelect
 * Allows you to pass an onChange function that uses OptionType rather than SelectItem<OptionType>
 */
export interface SingleItemSelectProps<OptionType>
  extends Omit<SelectItemSelectProps<OptionType, false>, 'onChange' | 'value' | 'options'> {
  /**
   * Menu options for the select
   *
   * If the options are objects and have `groupLabel` and `options` properties, they are assumed to be grouped
   */
  options: OptionType[] | OptionsGroup<OptionType>[];
  /*
   * Currently selected value
   */
  value: SingleValue<OptionType>;
  /**
   * Handle change events on the select
   */
  onChange: (newValue: OnChangeValue<OptionType, false>, actionMeta: ActionMeta<SelectItem<OptionType>>) => void;
}

function optionsAreGrouped<OptionType>(
  options: OptionType[] | OptionsGroup<OptionType>[]
): options is OptionsGroup<OptionType>[] {
  return options.some(
    (option) => typeof option === 'object' && !!option && 'groupLabel' in option && 'options' in option
  );
}

const mapOptions = <OptionType,>(
  optionsProp: OptionType[] | OptionsGroup<OptionType>[],
  getOptionValue: GetOptionValue<OptionType>
) => {
  const mapToOption = (option: OptionType) => ({ value: getOptionValue(option), data: option });
  if (optionsAreGrouped(optionsProp)) {
    const groups = optionsProp.map((optionGroup) => ({
      label: optionGroup.groupLabel,
      options: optionGroup.options.map(mapToOption),
    }));
    const allOptions = groups.flatMap((group) => group.options);
    return {
      options: groups,
      allOptions,
    };
  }
  const options = optionsProp.map(mapToOption);
  return {
    options,
    allOptions: options,
  };
};

/**
 * A wrapper for SelectItemSelect that supports single selection
 */
export const SingleItemSelect = <OptionType,>({
  onChange: onChangeProp,
  value: valueProp,
  options: optionsProp,
  getOptionValue,
  ...props
}: SingleItemSelectProps<OptionType>) => {
  const { options, allOptions } = mapOptions(optionsProp, getOptionValue);
  const value: SingleValue<SelectItem<OptionType>> =
    valueProp === null
      ? null
      : allOptions.find((selectItemOption) => selectItemOption.value === getOptionValue(valueProp)) || null;

  const onChange = (
    newValue: OnChangeValue<SelectItem<OptionType>, false>,
    actionMeta: ActionMeta<SelectItem<OptionType>>
  ) => {
    onChangeProp(newValue?.data ?? null, actionMeta);
  };

  return (
    <SelectItemSelect<OptionType, false>
      onChange={onChange}
      options={options}
      getOptionValue={getOptionValue}
      value={value}
      {...props}
    />
  );
};
